import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProductDocs } from '@/services/products';

export default function DocsPage() {
  const { slug } = useParams<{ slug: string }>();
  const [productName, setProductName] = useState('');
  const [docsHtml, setDocsHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getProductDocs(slug)
      .then((result) => {
        if (!result || !result.docsHtml) {
          setNotFound(true);
        } else {
          setProductName(result.name);
          setDocsHtml(result.docsHtml);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-surface-400 text-sm">Loading documentation…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-4 text-center px-4">
        <div className="w-14 h-14 rounded-full bg-surface-100 flex items-center justify-center">
          <i className="ri-file-unknow-line text-2xl text-surface-400" />
        </div>
        <h1 className="text-xl font-bold text-surface-900">No documentation found</h1>
        <p className="text-sm text-surface-500 max-w-sm">
          Documentation for <strong>{slug}</strong> hasn't been published yet.
        </p>
        <Link to="/products" className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors">
          Browse Products
        </Link>
      </div>
    );
  }

  if (!docsHtml) return null;

  return (
    <div className="flex flex-col" style={{ height: '100vh' }}>
      {/* Top bar */}
      <div className="shrink-0 border-b border-surface-200 bg-white z-10">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" className="shrink-0 text-surface-400 hover:text-surface-700 transition-colors">
              <i className="ri-home-4-line text-base" />
            </Link>
            <span className="text-surface-300">/</span>
            <Link to="/products" className="text-surface-400 hover:text-surface-700 text-sm transition-colors truncate">
              Products
            </Link>
            <span className="text-surface-300">/</span>
            <span className="text-surface-700 text-sm font-medium truncate">{productName} Docs</span>
          </div>
          <Link
            to={`/product/${slug}`}
            className="shrink-0 px-3 py-1.5 rounded-lg border border-surface-300 hover:border-brand-500 hover:text-brand-600 text-surface-600 text-xs font-medium transition-colors flex items-center gap-1.5"
          >
            <i className="ri-store-2-line" /> View Product
          </Link>
        </div>
      </div>

      {/* Docs iframe fills remaining height */}
      <iframe
        key={slug}
        srcDoc={docsHtml}
        title={`${productName} Documentation`}
        className="w-full border-0 flex-1"
        style={{ display: 'block' }}
      />
    </div>
  );
}
